import React, { useState, useEffect } from "react";
import { useAlert } from '../../providers/AlertProvider';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTruckArrowRight, faStopwatch, faClipboardList } from '@fortawesome/free-solid-svg-icons';
import { faEye } from '@fortawesome/free-regular-svg-icons';
import "../css/RouteContainer.css";

const formatTimeToHHMM = (timeString) => {
    if (!timeString) return null;
    if (timeString.length <= 5) return timeString;
    return timeString.substring(0, 5);
};

const ClassicRouteTable = ({
    routes,
    deleteRoute,
    removeDriver,
    removeVehicle,
    theme,
    onDragOver,
    onDrop,
    showJobComments,
    showJobCostings,
    showInvoiceTo,
    showStartTime,
    isLocked,
    showCostings,
    showAddFromDropdown,
    toggleConfirmed,
    vehicleServiceList,
    vehicleOverdueList,
    vorList,
    contractRoute,
    showDemurrage,
    completedRouteUuids,
    onViewCompletion,
    canEdit,
    editSubcontract,
    showDeliveryDetails,
    deliveryAlerts,
    onPodOff,
    moveRoute,
    showCustomerConsignment,
    showNetworkFreightMetrics,
}) => {
    const { showAlert } = useAlert();
    const vans = ['FE24_AYJ', 'FE24_AYK'];

    const [allRoutes, setAllRoutes] = useState([]);
    const [sortKey, setSortKey] = useState(null);
    const [podContextMenu, setPodContextMenu] = useState(null);
    const [moveUuid, setMoveUuid] = useState(null);
    const [moveDate, setMoveDate] = useState('');

    useEffect(() => {
        if (!podContextMenu) return;
        const close = () => setPodContextMenu(null);
        window.addEventListener('click', close);
        window.addEventListener('contextmenu', close);
        return () => {
            window.removeEventListener('click', close);
            window.removeEventListener('contextmenu', close);
        };
    }, [podContextMenu]);

    useEffect(() => {
        if (routes) {
            const sorted = sortKey ? filterBy(sortKey, routes) : [...routes].sort((a, b) => {
                if (a.is_subbed && !b.is_subbed) return 1;
                if (!a.is_subbed && b.is_subbed) return -1;
                return 0;
            });
            setAllRoutes(sorted);
        }
    }, [routes, sortKey]);

    const filterBy = (key, data) => {
        setSortKey(key);
        return [...data].sort((a, b) => {
            if (a.is_subbed && !b.is_subbed) return 1;
            if (!a.is_subbed && b.is_subbed) return -1;
            const valA = a[key] ?? null;
            const valB = b[key] ?? null;
            if (valA === null && valB !== null) return 1;
            if (valA !== null && valB === null) return -1;
            if (valA < valB) return -1;
            if (valA > valB) return 1;
            return 0;
        });
    };

    const handleRemoveDriver = (route_uuid, name) => {
        showAlert({
            message: `Remove ${name} from route?`,
            type: 'success',
            buttons: [
                { type: 'success', label: 'OK', onClick: () => removeDriver(route_uuid) },
                { type: 'secondary', label: 'Cancel' },
            ],
        });
    };

    const handleRemoveVehicle = (route_uuid, reg, type) => {
        showAlert({
            message: `Remove ${reg} from route?`,
            type: 'success',
            buttons: [
                { type: 'success', label: 'OK', onClick: () => removeVehicle(route_uuid, type) },
                { type: 'secondary', label: 'Cancel' },
            ],
        });
    };

    const handleRemoveJob = (route_uuid, job) => {
        showAlert({
            message: `What do you want to do with ${job}?`,
            type: 'warning',
            buttons: [
                { type: 'danger', label: 'Delete', onClick: () => deleteRoute(route_uuid) },
                { type: 'primary', label: 'Move', onClick: () => { setMoveDate(''); setMoveUuid(route_uuid); } },
                { type: 'secondary', label: 'Cancel' },
            ],
        });
    };

    const handleToggleConfirmed = (route_uuid, confirmed, isSubbed) => {
        showAlert({
            message: 'Change Status?',
            type: 'success',
            buttons: isSubbed ? [
                { type: 'primary', label: 'Unsub', onClick: () => contractRoute(route_uuid, false) },
                { type: 'warning', label: 'Edit', onClick: () => editSubcontract(route_uuid) },
                ...(canEdit ? [{ type: 'info', label: 'Pod', onClick: () => onPodOff?.(route_uuid) }] : []),
                { type: 'secondary', label: 'Cancel' },
            ] : [
                { type: 'success', label: confirmed ? 'Unconfirm' : 'Confirm', onClick: () => toggleConfirmed(route_uuid, confirmed) },
                { type: 'primary', label: 'Sub', onClick: () => contractRoute(route_uuid, true) },
                ...(canEdit ? [{ type: 'info', label: 'Pod', onClick: () => onPodOff?.(route_uuid) }] : []),
                { type: 'secondary', label: 'Cancel' },
            ],
        });
    };

    if (!routes || routes.length === 0) return null;

    return (
        <>
        {/* Move-to-date overlay */}
        {moveUuid && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                onClick={() => setMoveUuid(null)}
            >
                <div style={{ background: theme === 'dark' ? '#222' : '#fff', borderRadius: 10, padding: 24, minWidth: 280, boxShadow: '0 4px 20px rgba(0,0,0,0.4)' }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <h4 style={{ margin: '0 0 16px', color: theme === 'dark' ? '#fff' : '#222' }}>Move job to date</h4>
                    <input type="date" value={moveDate} onChange={(e) => setMoveDate(e.target.value)}
                        style={{ display: 'block', width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ccc', marginBottom: 16, fontSize: 15 }}
                    />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                        <button onClick={() => setMoveUuid(null)} style={{ padding: '7px 18px', borderRadius: 6, border: '1px solid #ccc', background: 'transparent', cursor: 'pointer', color: theme === 'dark' ? '#ccc' : '#555' }}>Cancel</button>
                        <button onClick={() => { if (moveDate) { moveRoute(moveUuid, moveDate); setMoveUuid(null); setMoveDate(''); } }} disabled={!moveDate} style={{ padding: '7px 18px', borderRadius: 6, border: 'none', background: moveDate ? '#6366f1' : '#aaa', color: '#fff', cursor: moveDate ? 'pointer' : 'not-allowed' }}>Move</button>
                    </div>
                </div>
            </div>
        )}
        <div id={theme} className={`route-container ${theme}`}>
            <table className="route-table">
                <thead>
                    <tr>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                            <FontAwesomeIcon style={{ width: 24, color: 'rgba(7,192,1,0.95)' }} icon={faCheck} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                            <FontAwesomeIcon style={{ width: 24, color: 'rgba(255,0,0,1)' }} icon={faStopwatch} />
                        </th>
                        <th className="fixed-th" style={{ background: 'rgba(0,0,0,0.3)' }}>
                            <FontAwesomeIcon style={{ width: 24, color: 'rgba(26,157,215,0.95)' }} icon={faClipboardList} />
                        </th>
                        <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('customer_company', allRoutes))}>Customer</th>
                        {showCustomerConsignment && <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('customer_consignment_number', allRoutes))}>Customer Ref</th>}
                        {showNetworkFreightMetrics && <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('pallet_count', allRoutes))}>Pallets</th>}
                        {showNetworkFreightMetrics && <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('weight', allRoutes))}>Weight</th>}
                        {showNetworkFreightMetrics && showCostings && <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('adr_value', allRoutes))}>ADR</th>}
                        <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('route', allRoutes))}>Route</th>
                        <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('job_comments', allRoutes))}>Job Comments</th>
                        <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('driver', allRoutes))}>Driver</th>
                        <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('truck_reg', allRoutes))}>Vehicle</th>
                        <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('trailer_reg', allRoutes))}>Trailer</th>
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Start</th>}
                        {showCostings && <th style={{ cursor: 'zoom-in', background: 'rgba(0,0,0,0.3)' }} onClick={() => setAllRoutes(filterBy('route_ref', allRoutes))}>Route Ref</th>}
                        {showCostings && <th style={{ background: 'rgba(0,0,0,0.3)' }}>Costings</th>}
                    </tr>
                </thead>
                <tbody>
                    {allRoutes.map((route, index) => {
                        const {
                            job, route_uuid, driver_id, driver,
                            truck_id, truck_reg, truck_type, trailer_id, trailer_reg, trailer_type,
                            job_comments, route: routeDetails, route_ref, accepted, completed,
                            external_trailer, costings, confirmed, invoice_id, is_subbed,
                            subbed_company, subbed_price, subbed_ref, demurrage, demurrage_cost, start_time,
                            customer_company, customer_consignment_number, pallet_count, weight, is_network_freight, adr_value,
                        } = route;

                        return (
                            <React.Fragment key={route_uuid || index}>
                                <tr
                                    data-driver-drop-route={!isLocked && !is_subbed ? route_uuid : undefined}
                                    className={`route-row ${accepted ? 'accepted' : ''} ${completed ? 'completed' : ''} ${showNetworkFreightMetrics && is_network_freight && adr_value ? 'network-adr-row' : ''}`}
                                    style={{
                                        background: completedRouteUuids?.[route_uuid]
                                            ? index % 2 === 0 ? 'rgba(26,157,215,0.35)' : 'rgba(56,189,248,0.28)'
                                            : is_subbed ? 'rgba(252,186,63,0.8)'
                                            : confirmed
                                                ? index % 2 === 0 ? 'rgba(26,221,247,0.8)' : 'rgba(26,221,247,0.6)'
                                                : theme === 'light'
                                                    ? index % 2 === 0 ? '#f9f9f9' : '#eaeaea'
                                                    : index % 2 === 0 ? '#3a3a3a' : '#2e2e2e',
                                        color: completedRouteUuids?.[route_uuid]
                                            ? theme === 'dark' ? '#f8f9fa' : '#333'
                                            : confirmed || is_subbed
                                                ? '#333'
                                                : theme === 'light' ? '#333' : '#ddd',
                                    }}
                                    draggable={false}
                                    onDragStart={(e) => e.preventDefault()}
                                    onDragOver={!isLocked ? onDragOver : undefined}
                                    onDrop={!isLocked ? (e) => onDrop(e, route, index) : undefined}
                                >
                                    {/* Eye / Truck */}
                                    <td
                                        title={completedRouteUuids?.[route_uuid] ? 'View Completion (right-click to Sub)' : (confirmed ? 'Confirmed' : 'Unconfirmed')}
                                        className="fixed-th"
                                        style={{ width: 40, cursor: completedRouteUuids?.[route_uuid] ? 'pointer' : isLocked ? 'not-allowed' : 'crosshair' }}
                                        onClick={() => {
                                            if (completedRouteUuids?.[route_uuid]) {
                                                onViewCompletion(route_uuid, { driver, truck_reg, trailer_reg, job_comments, route_ref });
                                            } else if (!isLocked) {
                                                handleToggleConfirmed(route_uuid, confirmed, is_subbed);
                                            }
                                        }}
                                        onContextMenu={(e) => {
                                            if (completedRouteUuids?.[route_uuid]) {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setPodContextMenu({ x: e.clientX, y: e.clientY, route_uuid });
                                            }
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            style={{
                                                width: 24,
                                                color: completedRouteUuids?.[route_uuid]
                                                    ? theme === 'dark' ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.9)'
                                                    : confirmed || is_subbed
                                                    ? 'rgba(0,0,0,0.9)'
                                                    : theme === 'dark' ? 'rgba(255,255,255,0.8)' : 'rgba(0,0,0,0.8)',
                                            }}
                                            icon={is_subbed ? faTruckArrowRight : faEye}
                                        />
                                    </td>

                                    {/* Demurrage */}
                                    <td title={demurrage ? `${demurrage}: £${demurrage_cost}` : 'Add Demurrage'}
                                        className="fixed-th"
                                        style={{ width: 40, cursor: isLocked ? 'not-allowed' : 'pointer' }}
                                        onClick={() => { if (!isLocked) showDemurrage(route_uuid); }}
                                    >
                                        <FontAwesomeIcon
                                            style={{ width: 24, color: demurrage ? '#ff0000' : confirmed ? 'rgba(0,0,0,0.9)' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                                            icon={faStopwatch}
                                        />
                                    </td>

                                    {/* Delivery Details */}
                                    <td className="fixed-th" title="View / Edit Delivery Details" style={{ width: 40, cursor: 'pointer' }} onClick={() => showDeliveryDetails(route_uuid)}>
                                        <FontAwesomeIcon
                                            icon={faClipboardList}
                                            style={{ width: 24, color: deliveryAlerts?.has(route_uuid) ? 'rgba(220,53,69,0.95)' : confirmed || is_subbed ? 'rgba(0,0,0,0.9)' : theme === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)' }}
                                        />
                                    </td>

                                    {/* Customer — route actions */}
                                    <td className={!isLocked ? 'cell-hoverable' : ''}>
                                        <span title={customer_company} style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked) handleRemoveJob(route_uuid, job); }}>
                                            {customer_company || '—'}
                                        </span>
                                    </td>

                                    {showCustomerConsignment && <td>{customer_consignment_number || '----'}</td>}
                                    {showNetworkFreightMetrics && <td>{pallet_count ?? '----'}</td>}
                                    {showNetworkFreightMetrics && <td>{weight == null ? '----' : `${Number(weight).toLocaleString('en-GB', { maximumFractionDigits: 2 })} kg`}</td>}
                                    {showNetworkFreightMetrics && showCostings && <td>{is_network_freight ? adr_value || '----' : '----'}</td>}

                                    {/* Route */}
                                    <td title={routeDetails}>{routeDetails || 'No route details'}</td>

                                    {/* Job Comments */}
                                    <td className={!isLocked ? 'cell-hoverable' : ''} style={{ maxWidth: 600, whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                        <span title={job_comments ?? ''} style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked) showJobComments(route_uuid, job_comments); }}>
                                            {job_comments || <span style={{ opacity: 0.35 }}>----</span>}
                                        </span>
                                    </td>

                                    {/* Driver */}
                                    {driver ? (
                                        <td className={!isLocked ? 'cell-hoverable' : ''}>
                                            <span style={{ cursor: (isLocked || is_subbed) ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked && !is_subbed) handleRemoveDriver(route_uuid, driver); }}>
                                                {driver}
                                            </span>
                                        </td>
                                    ) : (
                                        <td>
                                            <span style={{ cursor: (isLocked || is_subbed) ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked && !is_subbed) showAddFromDropdown(route_uuid, 'driver'); }}>
                                                {is_subbed ? 'Subbed out' : '----'}
                                            </span>
                                        </td>
                                    )}

                                    {/* Vehicle */}
                                    {truck_reg ? (
                                        <td className={(!isLocked && !is_subbed) ? 'cell-hoverable' : ''}
                                            style={{ backgroundColor: vans.includes(truck_reg) ? 'none' : vehicleServiceList.includes(truck_reg) ? 'rgba(253,191,58,0.6)' : vehicleOverdueList.includes(truck_reg) ? 'rgba(253,69,58,0.6)' : 'none' }}
                                        >
                                            <span style={{ cursor: isLocked || is_subbed ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked && !is_subbed) handleRemoveVehicle(route_uuid, truck_reg, 'truck'); }}>
                                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                                                    <span>{truck_reg}</span>
                                                    {vorList.includes(truck_reg) && <span style={{ fontWeight: 'bold' }}>(VOR)</span>}
                                                </div>
                                            </span>
                                        </td>
                                    ) : (
                                        <td>
                                            <span style={{ cursor: isLocked || is_subbed ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked && !is_subbed) showAddFromDropdown(route_uuid, 'truck'); }}>----</span>
                                        </td>
                                    )}

                                    {/* Trailer */}
                                    {trailer_reg ? (
                                        <td className={!isLocked ? 'cell-hoverable' : ''}
                                            style={{ backgroundColor: vehicleServiceList.includes(trailer_reg) ? 'rgba(253,191,58,0.6)' : vehicleOverdueList.includes(trailer_reg) ? 'rgba(253,69,58,0.6)' : 'none' }}
                                        >
                                            <span style={{ cursor: isLocked ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked) handleRemoveVehicle(route_uuid, trailer_reg, 'trailer'); }}>
                                                {trailer_id === 1 ? external_trailer : trailer_reg}
                                            </span>
                                        </td>
                                    ) : (
                                        <td>
                                            <span style={{ cursor: isLocked || is_subbed ? 'not-allowed' : 'pointer' }} onClick={() => { if (!isLocked && !is_subbed) showAddFromDropdown(route_uuid, 'trailer'); }}>----</span>
                                        </td>
                                    )}

                                    {/* Start Time */}
                                    {showCostings && (
                                        <td className="cell-hoverable">
                                            <span style={{ cursor: 'pointer' }} onClick={() => showStartTime(route_uuid, start_time)}>
                                                {formatTimeToHHMM(start_time) || '----'}
                                            </span>
                                        </td>
                                    )}

                                    {/* Consignment */}
                                    {showCostings && <td title={route_ref || 'No consignment number'}>{route_ref || '----'}</td>}

                                    {/* Costings */}
                                    {showCostings && (
                                        <td className="cell-hoverable">
                                            <span style={{ cursor: 'pointer' }} onClick={() => showJobCostings(route_uuid, costings)}>
                                                {costings ? '£' + costings : '----'}
                                            </span>
                                        </td>
                                    )}
                                </tr>

                                {is_subbed && (
                                    <tr style={{ background: theme === 'light' ? '#fff' : '#333' }}>
                                        <td colSpan={showCostings ? 11 : 8} style={{ padding: '12px 16px', color: '#a67c00', fontWeight: 500, fontSize: 15 }}>
                                            <span style={{ marginRight: 24 }}><strong>Subbed Out:</strong></span>
                                            <span style={{ marginRight: 24 }}>Company: <strong>{subbed_company || 'N/A'}</strong></span>
                                            <span style={{ marginRight: 24 }}>Ref: <strong>{subbed_ref || 'N/A'}</strong></span>
                                            <span>Price: <strong>£{subbed_price || '0.00'}</strong></span>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        );
                    })}
                </tbody>
            </table>
        </div>

        {podContextMenu && (
            <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', top: podContextMenu.y, left: podContextMenu.x, zIndex: 9999, background: '#fff', border: '1px solid #ccc', borderRadius: 6, boxShadow: '0 4px 16px rgba(0,0,0,0.18)', minWidth: 140, padding: '4px 0' }}>
                <div style={{ padding: '4px 12px 6px', fontSize: 11, color: '#888', borderBottom: '1px solid #eee', userSelect: 'none' }}>Podded Route</div>
                <div style={{ padding: '8px 16px', cursor: 'pointer', fontWeight: 600, color: '#0d6efd' }} onClick={() => { setPodContextMenu(null); contractRoute(podContextMenu.route_uuid, true); }}>Sub</div>
                <div style={{ padding: '8px 16px', cursor: 'pointer', color: '#555' }} onClick={() => setPodContextMenu(null)}>Cancel</div>
            </div>
        )}
        </>
    );
};

export default ClassicRouteTable;
